/**
 * §B-3 routing-rule editor — a Drawer form with the shared condition builder
 * (match ALL/ANY of leaf rows, NOT per leaf, raw-JSON escape hatch) plus
 * carrier/service/scope/priority fields. Saves via the ADMIN write API with
 * optimistic concurrency (a 409 means someone else changed it).
 */
import { useState } from "react";
import { ApiError, api, type AdminRoutingRule, type RoutingRuleInput } from "../../../lib/api.js";
import { ROUTING_FIELDS, conditionToBuilder, builderToCondition, validateBuilder, type BuilderState } from "../../../lib/conditionModel.js";
import { Drawer, Field, Input, Select, Button, useToast } from "../../../components/ui/index.js";
import { ConditionBuilder } from "../ConditionBuilder.js";

interface StoreOpt {
  id: string;
  label: string;
}

export function RuleEditor({
  rule,
  carrierCodes,
  stores,
  onClose,
  onSaved,
}: {
  rule: AdminRoutingRule | null;
  carrierCodes: string[];
  stores: StoreOpt[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const isNew = rule === null;

  const [name, setName] = useState(rule?.name ?? "");
  const [priority, setPriority] = useState(String(rule?.priority ?? 100));
  const [carrierCode, setCarrierCode] = useState(rule?.carrierCode ?? carrierCodes[0] ?? "");
  const [serviceCode, setServiceCode] = useState(rule?.serviceCode ?? "");
  const [scope, setScope] = useState<string>(rule?.storeId ?? "");
  const [enabled, setEnabled] = useState(rule?.enabled ?? true);
  const [builder, setBuilder] = useState<BuilderState>(() => conditionToBuilder(rule?.condition ?? { all: [] }));
  const [saving, setSaving] = useState(false);

  async function save() {
    const err = validateBuilder(builder, ROUTING_FIELDS);
    if (err) {
      toast(err, "danger");
      return;
    }
    if (!name.trim()) {
      toast("Give the rule a name.", "danger");
      return;
    }
    const prio = Number(priority);
    if (!Number.isInteger(prio)) {
      toast("Priority must be a whole number.", "danger");
      return;
    }
    let condition;
    try {
      condition = builderToCondition(builder, ROUTING_FIELDS);
    } catch {
      toast("Condition JSON is not valid.", "danger");
      return;
    }
    const input: RoutingRuleInput = {
      name: name.trim(),
      priority: prio,
      condition,
      carrierCode,
      serviceCode: serviceCode.trim(),
      enabled,
      storeId: scope || null,
    };
    setSaving(true);
    try {
      if (isNew) await api.admin.createRoutingRule(input);
      else await api.admin.updateRoutingRule(rule.id, input, rule.updatedAt);
      toast(isNew ? "Rule created" : "Rule saved", "success");
      onSaved();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        toast("This rule changed since you opened it — reloading.", "danger");
        onSaved();
      } else if (e instanceof ApiError && e.status === 400) {
        const msg = (e.detail as { error?: string } | undefined)?.error ?? "Invalid rule.";
        toast(msg, "danger");
      } else if (e instanceof ApiError && e.status === 403) {
        toast("Admin role required to edit rules.", "danger");
      } else {
        toast("Couldn’t save the rule.", "danger");
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer
      onClose={onClose}
      ariaLabel={isNew ? "New routing rule" : `Edit ${rule.name}`}
      title={isNew ? "New routing rule" : "Edit routing rule"}
      meta={<span className="muted">First matching rule (by priority) wins.</span>}
    >
      <div className="rule-form">
        <div className="rule-form__row2">
          <Field label="Name">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. EU express → DHL" autoFocus />
          </Field>
          <Field label="Priority (lower runs first)">
            <Input value={priority} onChange={(e) => setPriority(e.target.value)} inputMode="numeric" />
          </Field>
        </div>

        <div className="rule-form__row2">
          <Field label="Carrier">
            <Select
              ariaLabel="Carrier"
              value={carrierCode}
              onChange={setCarrierCode}
              options={carrierCodes.map((c) => ({ value: c, label: c }))}
            />
          </Field>
          <Field label="Service code">
            <Input value={serviceCode} onChange={(e) => setServiceCode(e.target.value)} placeholder="e.g. GLS_BUSINESS_PARCEL" />
          </Field>
        </div>

        <div className="rule-form__row2">
          <Field label="Scope">
            <Select
              ariaLabel="Scope"
              value={scope}
              onChange={setScope}
              options={[{ value: "", label: "All stores" }, ...stores.map((s) => ({ value: s.id, label: s.label }))]}
            />
          </Field>
          <Field label="Status">
            <label className="rule-form__toggle">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              <span>{enabled ? "Enabled" : "Disabled"}</span>
            </label>
          </Field>
        </div>

        <ConditionBuilder fields={ROUTING_FIELDS} builder={builder} onChange={setBuilder} />

        <div className="rule-form__actions">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" icon="check" onClick={() => void save()} disabled={saving}>
            {saving ? "Saving…" : isNew ? "Create rule" : "Save changes"}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
